/**
 * DataTable — Centralized table component
 *
 * Provides a consistent, reusable table with standardized:
 *  - Header styling (bg-muted/50, text-sm font-medium text-muted-foreground)
 *  - Row hover (hover:bg-muted/30 transition-colors)
 *  - Cell padding (px-4 py-3)
 *  - Empty state
 *  - Loading skeleton
 *  - Responsive overflow wrapper
 *
 * Usage:
 * ```tsx
 * <DataTable
 *   columns={[
 *     { key: 'name', header: 'Nombre' },
 *     { key: 'status', header: 'Estado', align: 'center' },
 *     { key: 'actions', header: 'Acciones', align: 'right' },
 *   ]}
 *   data={items}
 *   renderRow={(item) => (
 *     <DataTable.Row key={item.id}>
 *       <DataTable.Cell><span className="font-medium">{item.name}</span></DataTable.Cell>
 *       <DataTable.Cell align="center"><Badge>Activo</Badge></DataTable.Cell>
 *       <DataTable.Cell align="right"><DropdownMenu ... /></DataTable.Cell>
 *     </DataTable.Row>
 *   )}
 *   emptyMessage="No hay registros"
 *   isLoading={isLoading}
 *   skeletonRows={5}
 * />
 * ```
 */

import type { ReactNode, MouseEvent } from 'react';
import { motion, type Variants } from 'framer-motion';
import { spring } from '@/lib/motion';
import { EmptyState } from './EmptyState';

const tbodyVariants: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.025, delayChildren: 0.02 },
  },
};

const rowVariants: Variants = {
  hidden: { opacity: 0, y: 6 },
  visible: { opacity: 1, y: 0, transition: spring.gentle },
};

/* ------------------------------------------------------------------ */
/*  Column definition                                                  */
/* ------------------------------------------------------------------ */

export interface DataTableColumn {
  /** Unique key for the column */
  key: string;
  /** Header content — acepta string o ReactNode para casos con sort, checkbox, etc. */
  header: ReactNode;
  /** Text alignment — defaults to 'left' */
  align?: 'left' | 'center' | 'right';
  /** Optional className for the <th> */
  className?: string;
  /** Whether the column is only used as a narrow utility column (e.g. chevron) */
  shrink?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface DataTableProps<T> {
  /** Column definitions */
  columns: DataTableColumn[];
  /** Data array */
  data: T[];
  /** Render function for each row — must return <DataTable.Row> */
  renderRow: (item: T, index: number) => ReactNode;
  /** Optional render for expanded/accordion content below a row */
  renderExpandedRow?: (item: T, index: number) => ReactNode;
  /**
   * Mensaje principal cuando no hay datos. Se usa como título del
   * `EmptyState` global. Para estado vacío con descripción + acción
   * personalizadas, usa `emptyState` en su lugar.
   */
  emptyMessage?: string;
  /**
   * Estado vacío completo (tiene prioridad sobre `emptyMessage`). Úsalo
   * cuando necesites título + descripción + ícono custom o un botón
   * CTA para ayudar al usuario a llenar la tabla.
   */
  emptyState?: ReactNode;
  /** Whether data is loading */
  isLoading?: boolean;
  /** Number of skeleton rows to show while loading (default 5) */
  skeletonRows?: number;
  /** Extra className on the root <div> */
  className?: string;
}

/* ------------------------------------------------------------------ */
/*  Alignment helper                                                   */
/* ------------------------------------------------------------------ */

const alignClass = (align?: 'left' | 'center' | 'right') => {
  if (align === 'center') return 'text-center';
  // Columnas alineadas a la derecha son casi siempre numéricas (montos,
  // conteos): `tabular-nums` da ancho fijo por dígito para que las cifras
  // queden alineadas verticalmente en toda la columna.
  if (align === 'right') return 'text-right tabular-nums';
  return 'text-left';
};

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

interface RowProps {
  children: ReactNode;
  /** Extra classes (e.g. cursor-pointer for clickable rows) */
  className?: string;
  onClick?: () => void;
}

function Row({ children, className = '', onClick }: RowProps) {
  const isClickable = !!onClick;
  return (
    <motion.tr
      variants={rowVariants}
      whileHover={isClickable ? { backgroundColor: 'var(--color-muted)' } : undefined}
      transition={spring.snappy}
      className={`border-b border-border last:border-0 transition-colors duration-150 ${
        isClickable ? 'cursor-pointer hover:bg-muted/50' : 'hover:bg-muted/30'
      } ${className}`}
      role="row"
      onClick={onClick}
    >
      {children}
    </motion.tr>
  );
}

interface CellProps {
  children: ReactNode;
  align?: 'left' | 'center' | 'right';
  className?: string;
  /** Number of columns to span */
  colSpan?: number;
  /** Optional click handler (e.g. stopPropagation on actions column) */
  onClick?: (e: MouseEvent) => void;
}

function Cell({ children, align, className = '', colSpan, onClick }: CellProps) {
  return (
    <td
      className={`px-4 py-3 text-sm ${alignClass(align)} ${className}`}
      role="cell"
      colSpan={colSpan}
      onClick={onClick}
    >
      {children}
    </td>
  );
}

/** Expanded row that sits directly below its parent row (for accordion panels) */
interface ExpandedRowProps {
  children: ReactNode;
  colSpan: number;
}

function ExpandedRow({ children, colSpan }: ExpandedRowProps) {
  return (
    <tr role="row">
      <td role="cell" colSpan={colSpan} className="p-0">
        {children}
      </td>
    </tr>
  );
}

/* ------------------------------------------------------------------ */
/*  Loading skeleton                                                   */
/* ------------------------------------------------------------------ */

function Skeleton({
  columns,
  rows,
}: {
  columns: DataTableColumn[];
  rows: number;
}) {
  return (
    <div className="animate-pulse">
      {/* Header skeleton */}
      <div className="flex items-center border-b px-4 py-3 bg-muted/50">
        {columns.map((col) => (
          <div
            key={col.key}
            className={`h-3 bg-background-secondary rounded ${
              col.shrink ? 'w-8 mr-2' : 'flex-1 mx-2'
            }`}
          />
        ))}
      </div>
      {/* Row skeletons */}
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center border-b last:border-0 px-4 py-3.5"
        >
          {columns.map((col) => (
            <div
              key={col.key}
              className={`h-4 bg-background-secondary rounded ${
                col.shrink ? 'w-8 mr-2' : 'flex-1 mx-2'
              }`}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

function DataTableRoot<T>({
  columns,
  data,
  renderRow,
  emptyMessage = 'Sin registros',
  emptyState,
  isLoading = false,
  skeletonRows = 5,
  className = '',
}: DataTableProps<T>) {
  if (isLoading) {
    return <Skeleton columns={columns} rows={skeletonRows} />;
  }

  if (data.length === 0) {
    // Si el caller pasa un <EmptyState> ya construido, lo respetamos.
    // Si sólo pasa string, lo envolvemos en el componente global para
    // mantener consistencia visual con el resto del dashboard.
    return (
      <div className="py-6">
        {emptyState ?? (
          <EmptyState
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
            }
            title={emptyMessage}
          />
        )}
      </div>
    );
  }

  return (
    <div className={`overflow-x-auto ${className}`} role="table">
      <table className="w-full">
        <thead role="rowgroup">
          <tr className="border-b bg-muted/50" role="row">
            {columns.map((col) => (
              <th
                key={col.key}
                role="columnheader"
                className={`px-4 py-3 text-sm font-medium text-muted-foreground ${alignClass(col.align)} ${
                  col.shrink ? 'w-8' : ''
                } ${col.className ?? ''}`}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <motion.tbody
          role="rowgroup"
          variants={tbodyVariants}
          initial="hidden"
          animate="visible"
        >
          {data.map((item, index) => renderRow(item, index))}
        </motion.tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Sortable header sub-component                                      */
/* ------------------------------------------------------------------ */

interface SortableHeaderProps {
  /** Column key used to identify which column is being sorted */
  column: string;
  /** The currently sorted column key */
  currentSort?: string;
  /** Current sort direction */
  direction?: 'asc' | 'desc';
  /** Callback when this header is clicked */
  onSort?: (column: string) => void;
  children: ReactNode;
  className?: string;
}

function SortableHeader({
  column,
  currentSort,
  direction,
  onSort,
  children,
  className,
}: SortableHeaderProps) {
  const isActive = currentSort === column;

  return (
    <th
      className={`cursor-pointer select-none ${className || ''}`}
      role="columnheader"
      aria-sort={isActive ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={() => onSort?.(column)}
    >
      <div className="flex items-center gap-1">
        {children}
        {/* Indicador de orden en SVG (sistema: nunca glifos/emoji como \u00edcono).
            Activo asc/desc = chevron direccional; inactivo = doble chevron. */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          className={`h-3.5 w-3.5 shrink-0 ${
            isActive ? 'text-foreground' : 'text-foreground-tertiary'
          }`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {isActive ? (
            direction === 'asc' ? (
              <polyline points="6 14 12 8 18 14" />
            ) : (
              <polyline points="6 10 12 16 18 10" />
            )
          ) : (
            <>
              <polyline points="7 9 12 4 17 9" />
              <polyline points="7 15 12 20 17 15" />
            </>
          )}
        </svg>
      </div>
    </th>
  );
}

/* ------------------------------------------------------------------ */
/*  Export as compound component                                       */
/* ------------------------------------------------------------------ */

export const DataTable = Object.assign(DataTableRoot, {
  Row,
  Cell,
  ExpandedRow,
  SortableHeader,
});
